import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function StatCard({ label, value }) {
  return (
    <div className="col-sm-3 col-6">
      <div className="p-16 text-center" style={{ background: '#f6faf8', borderRadius: 12, border: '1px solid #e8f5ee' }}>
        <div className="text-24 fw-bold text-main-600">{value ?? '—'}</div>
        <div className="text-13 text-gray-500">{label}</div>
      </div>
    </div>
  );
}

function ExamPerformanceTab() {
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getToken = () => JSON.parse(sessionStorage.getItem('adminSession'))?.token;

  useEffect(() => {
    (async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${apiUrl}/api/admin/mock-exams`, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) setExams(response.data.exams || []);
      } catch (err) { /* non-fatal */ }
    })();
  }, []);

  const fetchReport = useCallback(async (id) => {
    if (!id) { setReport(null); return; }
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams/${id}/report`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setReport(response.data.report);
      else setError(response.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching report.');
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className="card">
      <div className="card-body">
        <div className="mb-20">
          <label className="text-13 text-gray-500 mb-4 d-block">Select Exam</label>
          <select className="form-control py-9" style={{ maxWidth: 400 }} value={examId} onChange={(e) => { setExamId(e.target.value); fetchReport(e.target.value); }}>
            <option value="">Select an exam...</option>
            {exams.map((e) => (<option key={e.id} value={e.id}>{e.title} ({e.coursename})</option>))}
          </select>
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading && <div className="text-center py-20"><div className="spinner-border text-main-600" /></div>}
        {report && (
          <div className="row g-16">
            <StatCard label="Eligible Students" value={report.eligible_students} />
            <StatCard label="Attempted" value={report.students_attempted} />
            <StatCard label="Not Attempted" value={report.students_not_attempted} />
            <StatCard label="Total Attempts" value={report.total_attempts} />
            <StatCard label="Passed" value={report.passed} />
            <StatCard label="Failed" value={report.failed} />
            <StatCard label="Pending Evaluation" value={report.pending_evaluations} />
            <StatCard label="Average Marks" value={report.average_marks} />
            <StatCard label="Highest Marks" value={report.highest_marks} />
            <StatCard label="Lowest Marks" value={report.lowest_marks} />
            <StatCard label="Average %" value={report.average_percentage} />
          </div>
        )}
      </div>
    </div>
  );
}

function StudentPerformanceTab() {
  const [studentId, setStudentId] = useState('');
  const [student, setStudent] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getToken = () => JSON.parse(sessionStorage.getItem('adminSession'))?.token;

  const fetchReport = async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams/students/${studentId}/report`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setStudent(response.data.student);
        setExams(response.data.exams || []);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching student report.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex gap-8 mb-20">
          <input type="number" className="form-control py-9" placeholder="Enter numeric Student ID (studentprofile.id)" style={{ maxWidth: 320 }}
            value={studentId} onChange={(e) => setStudentId(e.target.value)} />
          <button type="button" className="btn btn-main rounded-pill py-9 px-20" onClick={fetchReport}>Search</button>
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading && <div className="text-center py-20"><div className="spinner-border text-main-600" /></div>}
        {student && (
          <>
            <div className="fw-semibold mb-12">{student.firstName} {student.lastName} ({student.studentid})</div>
            {exams.length === 0 ? (
              <div className="text-gray-400 text-center py-20">No mock exam attempts found for this student.</div>
            ) : (
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>Exam</th><th>Course</th><th>Attempts</th><th>Best Mark</th><th>Latest Mark</th><th>Average</th><th>Pass</th><th>Fail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exams.map((e, idx) => (
                      <tr key={idx}>
                        <td>{e.exam_title}</td><td>{e.coursename}</td><td>{e.attempts_count}</td>
                        <td>{e.best_mark ?? '—'}</td><td>{e.latest_mark ?? '—'}</td><td>{e.average_mark ?? '—'}</td>
                        <td>{e.pass_count}</td><td>{e.fail_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AdminMockExamReports() {
  const [tab, setTab] = useState('exam');

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Reports</span></li>
              </ul>
            </div>
          </div>

          <ul className="nav nav-tabs mb-20">
            <li className="nav-item"><button type="button" className={`nav-link ${tab === 'exam' ? 'active' : ''}`} onClick={() => setTab('exam')}>Exam Performance</button></li>
            <li className="nav-item"><button type="button" className={`nav-link ${tab === 'student' ? 'active' : ''}`} onClick={() => setTab('student')}>Student Performance</button></li>
          </ul>

          {tab === 'exam' ? <ExamPerformanceTab /> : <StudentPerformanceTab />}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminMockExamReports;
